import classNames from "classnames";
import Popover from "components/Popover";
import Tag from "components/Tag";
import useLabels from "hooks/useLabels";
import { useSessionStorageState } from "hooks/useSessionStorageState";
import { useEffect, useState } from "react";
import { FailedMeterContent } from "./components/FailedMeterContent";
import { HeaderTagTooltip } from "./components/HeaderTagTooltip";
import { MeterContent } from "./components/MeterContent";
import { MeterFilter } from "./components/MeterFilter";
import { SearchParams, useConsensusMeter } from "./hooks/useConsensusMeter";
import { useConsensusMeterFilter } from "./hooks/useConsensusMeterFilter";

interface ConsensusMeterProps {
  /**
   * The search ID params (thread search ID or non-thread search ID) to pass to the consensus meter.
   */
  searchParams: SearchParams;
  /**
   * The ID of the thread interaction used to render the consensus meter.
   */
  threadInteractionId?: string;
  /**
   * The thread ID for the current thread if in threaded search
   */
  threadId: string;
  /**
   * Optional query title to replace the default Consensus Meter title.
   */
  queryTitle?: string;
  /**
   * Whether the consensus meter is embedded in a markdown content block.
   */
  markdownEmbedded?: boolean;
  /**
   * Whether the consensus meter is streaming.
   */
  markdownStreaming?: boolean;
  /**
   * Whether to show loading state of the meter.
   */
  showLoadingState?: boolean;
  /**
   * Generated query override for the consensus meter from the LLM.
   */
  generatedQueryOverride?: string;
  /**
   * The original user message that was used to generate the analysis.
   */
  originalUserMessage?: string;
  /**
   * Whether this is the first element in the markdown content.
   */
  isFirstElement?: boolean;
}

const CONSENSUS_METER_SNAPSHOT_SESSION_STORAGE_KEY_PREFIX =
  "consensusMeterSnapshot";

const ConsensusMeter = ({
  searchParams,
  threadInteractionId,
  threadId,
  queryTitle,
  markdownEmbedded,
  showLoadingState = true,
  generatedQueryOverride,
  originalUserMessage,
  isFirstElement = false,
}: ConsensusMeterProps) => {
  const [consensusMeterTitle] = useLabels("meter.title");
  let queryOverride =
    generatedQueryOverride || queryTitle || originalUserMessage;
  let renderedTitle = queryOverride;

  if (queryOverride === originalUserMessage) {
    renderedTitle = consensusMeterTitle;
  }

  const {
    consensusMeterSuccess,
    consensusMeterResults,
    isLoading,
    hasMeterFailed,
  } = useConsensusMeter({
    searchParams: searchParams,
    queryOverride: queryOverride,
  });

  const { resetMeterFilter } = useConsensusMeterFilter(threadInteractionId);
  const [isFilteringEnabled, setIsFilteringEnabled] = useState(false);

  const searchId = searchParams.searchId;
  const [isSnapshotOpen, setIsSnapshotOpen] = useSessionStorageState(
    `${CONSENSUS_METER_SNAPSHOT_SESSION_STORAGE_KEY_PREFIX}-${searchId}`,
    false
  );

  // TODO: (alan) This is required to reset the filter when the component is unmounted
  // when filtering reduces the number of papers to zero. We should differentiate between
  // the number of papers being zero due to client side filtering and the number of papers
  // being zero due to the server returning no papers.
  useEffect(() => {
    return () => {
      resetMeterFilter();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Centralized loading state logic
  if (!showLoadingState && isLoading) {
    return null;
  }

  return (
    <div
      className={classNames(
        "mb-4",
        markdownEmbedded && "not-prose",
        !isSnapshotOpen && "border-b border-border-base"
      )}
      data-testid="consensus-meter"
    >
      <div
        className={classNames(
          "flex flex-col mb-6",
          markdownEmbedded && "mt-0",
          !isSnapshotOpen && "mb-4"
        )}
      >
        {/* Header */}
        {/* Title, tag and filter */}
        <div
          className={classNames(
            "flex items-center justify-between min-h-10",
            markdownEmbedded && isFirstElement && "mt-[-12px]"
          )}
        >
          <div className="flex gap-2 items-center mr-2">
            <p className="base-bold line-clamp-2 overflow-hidden text-ellipsis">
              {renderedTitle}
            </p>
            {!!consensusMeterResults?.num_results_analyzed && (
              <Popover tooltipContent={<HeaderTagTooltip />} interactive>
                <Tag
                  label={`N = ${consensusMeterResults.num_results_analyzed}`}
                  size="sm"
                  className="shrink-0"
                />
              </Popover>
            )}
          </div>
          {!hasMeterFailed && (
            <div className="flex shrink-0">
              <MeterFilter
                onFilterToggle={() =>
                  setIsFilteringEnabled(!isFilteringEnabled)
                }
                isFilteringEnabled={isFilteringEnabled}
                disabled={!consensusMeterSuccess}
                threadInteractionId={threadInteractionId}
              />
            </div>
          )}
        </div>
        {/* Meter content */}
        {hasMeterFailed ? (
          <FailedMeterContent searchParams={searchParams} threadId={threadId} />
        ) : (
          <MeterContent
            consensusMeterSuccess={consensusMeterSuccess}
            consensusMeterResults={consensusMeterResults}
            isLoading={isLoading}
            hasMeterFailed={hasMeterFailed}
            isFilteringEnabled={isFilteringEnabled}
            threadInteractionId={threadInteractionId}
            isSnapshotOpen={isSnapshotOpen}
            setIsSnapshotOpen={setIsSnapshotOpen}
            searchId={searchId}
          />
        )}
      </div>
    </div>
  );
};

export default ConsensusMeter;
